import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideDownload,
  lucideFileImage,
  lucideFileText,
  lucideBox,
  lucideFile,
} from '@ng-icons/lucide';
import { HlmBadgeDirective } from '@spartan-ng/helm/badge';
import { FileOverviewItem } from '@shared/models/analysis.model';
import {
  ColumnDef,
  DataTableComponent,
} from '@shared/components/data-table/data-table';
import { AnalysisDetailStore } from '@features/analyses/store/analysis-detail.store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { AnalysisService } from '@shared/services/analysis.service';
import { ToastService } from '@shared/services/toast.service';

/** Maps a raw file tag to a German display label */
function getFileTagLabel(tag: string): string {
  const labels: Record<string, string> = {
    satellite_image: 'Satellitenbild',
    satellite_image_with_pv: 'Satellitenbild mit PV',
    satellite_image_with_marker: 'Satellitenbild mit Marker',
    '3d_model': '3D-Modell',
    ply_3d_model: '3D-Modell',
    xyz_3d_model: 'Punktwolke',
    '3d_model_with_pv': '3D-Modell mit PV',
    elevation_model: 'Höhenmodell',
    point_cloud: 'Punktwolke',
    pdf_report: 'PDF-Bericht',
    planning_result: 'Planungsergebnis',
    export: 'Export',
  };
  return labels[tag] ?? tag;
}

/** Maps a MIME content type to a lucide icon name */
function getFileIcon(contentType?: string): string {
  if (!contentType) return 'lucideFile';
  if (contentType.startsWith('image/')) return 'lucideFileImage';
  if (contentType === 'application/pdf') return 'lucideFileText';
  if (
    contentType.includes('octet-stream') ||
    contentType.includes('model') ||
    contentType.includes('ply')
  )
    return 'lucideBox';
  return 'lucideFile';
}

/** Formats a byte count to a human-readable string */
function formatFileSize(bytes?: number): string {
  if (bytes == null || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Formats an ISO date string to German locale */
function formatUploadDate(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

@Component({
  selector: 'app-analysis-files-tab',
  standalone: true,
  imports: [
    NgIconComponent,
    HlmBadgeDirective,
    DataTableComponent,
  ],
  providers: [
    provideIcons({
      lucideDownload,
      lucideFileImage,
      lucideFileText,
      lucideBox,
      lucideFile,
    }),
  ],
  templateUrl: './analysis-files-tab.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisFilesTab {
  private readonly analysisService = inject(AnalysisService);
  private readonly store = inject(AnalysisDetailStore);
  private readonly orgStore = inject(CurrentOrganizationStore);
  private readonly toastService = inject(ToastService);

  filesOverview = input<FileOverviewItem[]>([]);
  selectedMediaId = input<string | null>(null);
  isLoading = input(false);

  /** Tag currently being fetched for download (shows spinner on that row) */
  readonly downloadingTag = signal<string | null>(null);

  readonly existingFiles = computed(() =>
    this.filesOverview().filter((f) => f.exists),
  );

  @ViewChild('fileTpl', { static: true }) fileTpl!: TemplateRef<unknown>;
  @ViewChild('downloadTpl', { static: true }) downloadTpl!: TemplateRef<unknown>;

  columns: ColumnDef<FileOverviewItem>[] = [];

  readonly rowClass = (file: FileOverviewItem): string =>
    this.isSelected(file) ? 'bg-primary/[0.03]' : '';

  ngOnInit(): void {
    this.columns = [
      {
        header: 'Datei',
        template: this.fileTpl,
        searchValue: (file) => file.file?.filename ?? this.getLabel(file.tag),
        sortValue: (file) => file.file?.filename ?? this.getLabel(file.tag),
      },
      {
        header: 'Typ',
        cell: (file) => this.getLabel(file.tag),
        sortValue: (file) => this.getLabel(file.tag),
      },
      {
        header: 'Größe',
        cell: (file) => this.getSize(file) || '—',
        sortValue: (file) => file.file?.size,
        class: 'text-right font-mono text-xs',
      },
      {
        header: 'Erzeugt',
        cell: (file) => this.getDate(file) || '—',
        sortValue: (file) => file.file?.uploadedAt,
        class: 'text-right font-mono text-xs',
      },
      { header: '', template: this.downloadTpl, class: 'w-12 text-right' },
    ];
  }

  isSelected(file: FileOverviewItem): boolean {
    const mediaId = this.selectedMediaId();
    if (!mediaId) return false;
    return (file.file?.id ?? file.file?.key) === mediaId;
  }

  getLabel(tag: string): string {
    return getFileTagLabel(tag);
  }

  getIcon(file: FileOverviewItem): string {
    return getFileIcon(file.file?.contentType);
  }

  getSize(file: FileOverviewItem): string {
    return formatFileSize(file.file?.size);
  }

  getDate(file: FileOverviewItem): string {
    return formatUploadDate(file.file?.uploadedAt);
  }

  async openDownload(file: FileOverviewItem): Promise<void> {
    if (!this.orgStore.can('analysis', 'viewDetails')) return;
    if (this.downloadingTag()) return;

    const orgId = this.orgStore.organization()?.id;
    const analysisId = this.store.lastLoadedId();

    if (!orgId || !analysisId) {
      this.toastService.warning('Keine Download-URL verfügbar');
      return;
    }

    // Resolve a fresh URL in the file’s original planning scope.
    this.downloadingTag.set(file.tag);
    try {
      const fresh = await this.analysisService.getFileDownloadUrl(orgId, analysisId, file.tag, file.file?.planningId);
      if (!fresh.downloadUrl) throw new Error('Download-URL fehlt');
      const response = await fetch(fresh.downloadUrl);
      if (!response.ok) throw new Error(`Download fehlgeschlagen (${response.status})`);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = file.file?.filename || file.tag;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      this.toastService.error('Download konnte nicht gestartet werden');
    } finally {
      this.downloadingTag.set(null);
    }
  }
}
